import { useState, useRef, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "./Register.css";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { config } from "../Environment";
import { WSContext } from "../App";
import { directionService } from "../services/directionService";
import { trackRegistration } from "../lib/analytics";
import { trackRegistrationWithData } from "../lib/appTracking";
import { Eye, EyeOff, AlertCircle, CheckCircle2, ArrowLeft, Sparkles, Shield, Zap, Route, Users } from "lucide-react";
import UserTypeCards from "./userType/UserTypeCards";
import { SEEKER_TYPE, EXPERT_TYPE, ORGANIZATION_TYPE } from "../lib/userType";

import signInGoogle from "../images/SignInGoogle.png";

import { Spinner } from "./ui/spinner";

function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const location = useLocation();
  const acceptId = location.state?.acceptId;

  const displayNameRef = useRef();
  const emailRef = useRef();

  const { t } = useTranslation();
  const { socket } = useContext(WSContext);

  const [authMethodsReady, setAuthMethodsReady] = useState(false);
  const [authMethods, setAuthMethods] = useState([]);

  const collectionId = searchParams.get('collection');
  // Mentor referral signup: token from /join-mentor → attaches the new user to the
  // mentor's segment and assigns its onboarding program on the backend.
  const segmentToken = searchParams.get('mentor');
  // PWF collaboration invite: token from the collaboration invite email.
  const pwfInviteToken = searchParams.get('pwfInvite');
  const [collectionName, setCollectionName] = useState("");
  const [collectionLevel, setCollectionLevel] = useState(null);
  const [loadingCollectionInfo, setLoadingCollectionInfo] = useState(false);

  async function getAuthMethods() {
    const response = await fetch(config.API_BASE + "/api/auth/methods");
    if (response.ok) {
      const responseJSON = await response.json();
      if (Array.isArray(responseJSON.list)) {
        return responseJSON.list;
      }
    }
    return [];
  }

  useEffect(() => {
    (async function displayAuthMethods() {
      const result = await getAuthMethods();
      setAuthMethods(result);
      setAuthMethodsReady(true);
    })();
  }, []);

  useEffect(() => {
    if (authMethodsReady && authMethods.includes('password')) {
      displayNameRef.current?.focus();
    }
  }, [authMethods, authMethodsReady]);

  useEffect(() => {
    if (collectionId) {
      const fetchCollectionInfo = async () => {
        try {
          setLoadingCollectionInfo(true);
          const response = await fetch(`${config.API_BASE}/api/collections/info/${collectionId}`);
          if (response.ok) {
            const data = await response.json();
            setCollectionName(data.name || "");
            setCollectionLevel(data.level || null);
          }
        } catch (error) {
          console.error('Failed to fetch collection info:', error);
        } finally {
          setLoadingCollectionInfo(false);
        }
      };
      fetchCollectionInfo();
    }
  }, [collectionId]);

  const getLevelLabel = (level) => {
    if (level === 10) return t('department') || 'Department';
    if (level === 20) return t('course') || 'Course';
    if (level === 30) return t('class') || 'Class';
    return t('group') || 'Group';
  };

  useEffect(() => {
    let directionUnsubscribe = directionService
      .getDirectionChange()
      .subscribe(() => {
        setDirection(document.body.dir);
      });

    return () => {
      directionUnsubscribe.unsubscribe();
    };
  }, []);

  // Audience fork ("three gates"): direct signups choose who they are before the
  // form; invite flows (collection / mentor segment / pwf collaboration) are
  // already targeted, so they skip the fork and register as seekers.
  // The first-minute funnel (/start) is a seeker by definition, like invite flows.
  const isStartFlow = searchParams.get("from") === "start";
  const isInviteFlow = !!(collectionId || segmentToken || pwfInviteToken || acceptId) || isStartFlow;
  const [userType, setUserType] = useState(null);
  const effectiveUserType = isInviteFlow ? SEEKER_TYPE : userType;
  const showFork = !isInviteFlow && !userType;

  // The branding panel mirrors the chosen gate: coach signups see the Studio pitch
  const isCoachSignup = effectiveUserType === EXPERT_TYPE;

  const handleUserTypeSelect = (type) => {
    if (type === ORGANIZATION_TYPE) {
      // Organizations talk to us instead of self-serve signup
      navigate("/contact");
      return;
    }
    setUserType(type);
  };

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [direction, setDirection] = useState(document.body.dir);

  useEffect(() => {
    setErrMsg("");
    setSuccess(false);
  }, [displayName, email, pwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!displayName) {
      displayNameRef.current.focus();
      setLoading(false);
      setErrMsg(t("msg_error_name_required"));
      return;
    }

    if (!email || !pwd) {
      emailRef.current.focus();
      setLoading(false);
      setErrMsg(t("msg_error_email_and_password_required"));
      return;
    }

    if (pwd !== pwd2) {
      setLoading(false);
      setErrMsg(t("msg_error_passwords_dont_match"));
      return;
    }

    if (!acceptedTerms) {
      setLoading(false);
      setErrMsg(t("msg_error_must_accept_terms") || "You must accept the Terms of Service and Privacy Policy");
      return;
    }

    setLoading(true);

    let response = await fetch(config.API_BASE + "/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        displayName,
        email,
        password: pwd,
        acceptedTerms,
        collectionId: collectionId || undefined,
        segmentToken: segmentToken || undefined,
        pwfInviteToken: pwfInviteToken || undefined,
        userType: effectiveUserType || undefined,
      }),
      credentials: "include",
    });

    if (!response.ok) {
      setLoading(false);
      setErrMsg(t("msg_error_already_registered"));
      emailRef.current.focus();
      return;
    }

    setDisplayName("");
    setEmail("");
    setPwd("");
    setPwd2("");
    setSuccess(true);

    trackRegistration();
    trackRegistrationWithData({ email, displayName });

    const data = await response.json();
    let accessToken = data?.accessToken;

    const returnUrl = searchParams.get('return');
    const fromWizard = searchParams.get('from_wizard') === 'true';

    // Coaches land on the plans page right after signup — their 30-day Studio
    // trial is already active; this is where they see what it includes.
    const postSignupUrl =
      effectiveUserType === EXPERT_TYPE && !returnUrl ? "/plans?welcome=1" : returnUrl;

    const navState = {
      email,
      accessToken,
      from: location.state?.from,
      engagementTransfer: location.state?.engagementTransfer,
      pendingAction: location.state?.pendingAction,
      acceptId,
      returnUrl: postSignupUrl,
      fromWizard,
    };

    if (location.state?.engagementTransfer) {
      sessionStorage.removeItem("prescriptionID");
      sessionStorage.removeItem("processID");
      sessionStorage.removeItem("runProcessID");
    }

    window.setTimeout(() => {
      const loginUrl = fromWizard
        ? `/login?t=${Date.now()}&return=${encodeURIComponent(returnUrl || '')}&from_wizard=true`
        : `/login?t=${Date.now()}`;
      navigate(loginUrl, { state: navState, replace: true });
    }, 2000);
  };

  const changeDisplayName = (e) => setDisplayName(e.target.value);
  const changeEmail = (e) => setEmail(e.target.value);
  const changePassword = (e) => setPwd(e.target.value);
  const changePassword2 = (e) => setPwd2(e.target.value);

  function renderLoadingAuthMethods() {
    if (authMethodsReady) return null;
    return (
      <div className="flex justify-center py-4">
        <Spinner size="md" />
      </div>
    );
  }

  function renderRegistrationDisabled() {
    if (authMethodsReady && authMethods.length === 0) {
      return (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{t("registration_temporarily_disabled") || "Registration is temporarily disabled. Please try again later."}</p>
        </div>
      );
    }
    return null;
  }

  function renderRegisterWithPassword() {
    if (authMethodsReady && authMethods.includes('password')) {
      return (
        <form onSubmit={handleSubmit} noValidate>
          <div className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="displayName">{t("display_name")}</Label>
              <Input
                id="displayName"
                type="text"
                placeholder={t("display_name")}
                onChange={changeDisplayName}
                ref={displayNameRef}
                value={displayName}
                autoComplete="name"
                required
                className="h-11"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="email">{t("email_address")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                onChange={changeEmail}
                ref={emailRef}
                value={email}
                autoComplete="email"
                required
                className="h-11"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="password">{t("password")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  onChange={changePassword}
                  value={pwd}
                  autoComplete="new-password"
                  required
                  className="h-11 pe-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 end-0 flex items-center pe-3 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? t("hide_password") || "Hide password" : t("show_password") || "Show password"}
                  style={{ touchAction: "manipulation" }}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="password2">{t("password_repeat")}</Label>
              <div className="relative">
                <Input
                  id="password2"
                  type={showPassword2 ? "text" : "password"}
                  placeholder="••••••••"
                  onChange={changePassword2}
                  value={pwd2}
                  autoComplete="new-password"
                  required
                  className="h-11 pe-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword2((v) => !v)}
                  className="absolute inset-y-0 end-0 flex items-center pe-3 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword2 ? t("hide_password") || "Hide password" : t("show_password") || "Show password"}
                  style={{ touchAction: "manipulation" }}
                >
                  {showPassword2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-start gap-2.5 pt-1">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked)}
                className="mt-0.5"
              />
              <label
                htmlFor="terms"
                className="text-sm leading-relaxed cursor-pointer text-muted-foreground"
              >
                {t("i_accept_the") || "I accept the"}{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  className="font-medium text-foreground underline-offset-4 hover:underline"
                >
                  {t("terms_of_service") || "Terms of Service"}
                </Link>
                {" "}{t("and") || "and"}{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  className="font-medium text-foreground underline-offset-4 hover:underline"
                >
                  {t("privacy_policy") || "Privacy Policy"}
                </Link>
              </label>
            </div>

            {errMsg && (
              <div
                role="alert"
                aria-live="polite"
                className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{errMsg}</span>
              </div>
            )}

            {success && (
              <div
                role="status"
                aria-live="polite"
                className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-800"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t("msg_register_successful")}</span>
              </div>
            )}

            <Button
              type="submit"
              className="h-11 w-full text-base font-medium"
              disabled={loading}
              style={{ touchAction: "manipulation" }}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" />
                  {t("creating_account") || "Creating account…"}
                </span>
              ) : (
                t("sign_up")
              )}
            </Button>
          </div>
        </form>
      );
    }
    return null;
  }

  function renderRegisterWithGoogle() {
    if (authMethodsReady && authMethods.includes('google')) {
      return (
        <button
          type="button"
          onClick={() => {
            if (location.state) {
              sessionStorage.setItem("locationstate", JSON.stringify(location.state));
            }
            window.open(`${config.API_BASE}/auth/google/redirect`, "_self");
          }}
          className="flex h-11 w-full items-center justify-center gap-3 rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{ touchAction: "manipulation" }}
        >
          <img src={signInGoogle} alt="" className="h-5 w-5" />
          {t("signup_with_google")}
        </button>
      );
    }
    return null;
  }

  const hasPasswordMethod = authMethodsReady && authMethods.includes("password");
  const hasGoogleMethod = authMethodsReady && authMethods.includes("google");
  const showDivider = hasPasswordMethod && hasGoogleMethod;

  return (
    <div className="min-h-dvh flex">
      {/* Left branding panel — desktop only */}
      <div
        className="hidden lg:flex lg:w-[480px] xl:w-[560px] shrink-0 flex-col justify-between p-12 text-white"
        style={{ background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.75) 100%)" }}
        aria-hidden="true"
      >
        {/* Friendly illustration — welcoming figure */}
        <div className="flex justify-center">
          <svg viewBox="0 0 280 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px]" aria-hidden="true">
            {/* Pulsing glow */}
            <circle cx="140" cy="105" r="85" fill="white" fillOpacity="0.05">
              <animate attributeName="r" values="85;93;85" dur="4s" repeatCount="indefinite"/>
              <animate attributeName="fill-opacity" values="0.05;0.09;0.05" dur="4s" repeatCount="indefinite"/>
            </circle>
            {/* Person figure — head ring (pulsing) */}
            <circle cx="140" cy="55" r="22" fill="white" fillOpacity="0.15" stroke="white" strokeOpacity="0.4" strokeWidth="2">
              <animate attributeName="r" values="22;26;22" dur="3.5s" repeatCount="indefinite"/>
              <animate attributeName="stroke-opacity" values="0.4;0.1;0.4" dur="3.5s" repeatCount="indefinite"/>
            </circle>
            {/* Head */}
            <circle cx="140" cy="55" r="13" fill="white" fillOpacity="0.85"/>
            {/* Body arc */}
            <path d="M 105 120 Q 140 90 175 120" stroke="white" strokeOpacity="0.5" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
            {/* Arms spread wide — welcoming */}
            <path d="M 112 105 Q 85 95 68 110" stroke="white" strokeOpacity="0.5" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
            <path d="M 168 105 Q 195 95 212 110" stroke="white" strokeOpacity="0.5" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
            {/* Hand circles */}
            <circle cx="65" cy="112" r="6" fill="white" fillOpacity="0.7"/>
            <circle cx="215" cy="112" r="6" fill="white" fillOpacity="0.7"/>
            {/* Sparkle left — floating */}
            <g>
              <path d="M 55 60 l2 5 l2 -5 l5 -2 l-5 -2 l-2 -5 l-2 5 l-5 2 z" fill="white" fillOpacity="0.6">
                <animateTransform attributeName="transform" type="translate" values="0,0;0,-8;0,0" dur="3s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"/>
                <animate attributeName="fill-opacity" values="0.6;1;0.6" dur="3s" repeatCount="indefinite"/>
              </path>
            </g>
            {/* Sparkle right — floating */}
            <g>
              <path d="M 220 58 l1.5 3.5 l1.5 -3.5 l3.5 -1.5 l-3.5 -1.5 l-1.5 -3.5 l-1.5 3.5 l-3.5 1.5 z" fill="white" fillOpacity="0.5">
                <animateTransform attributeName="transform" type="translate" values="0,0;0,-6;0,0" dur="2.8s" begin="0.7s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"/>
                <animate attributeName="fill-opacity" values="0.5;0.9;0.5" dur="2.8s" begin="0.7s" repeatCount="indefinite"/>
              </path>
            </g>
            {/* Sparkle top — floating */}
            <g>
              <path d="M 165 28 l1.5 3.5 l1.5 -3.5 l3.5 -1.5 l-3.5 -1.5 l-1.5 -3.5 l-1.5 3.5 l-3.5 1.5 z" fill="white" fillOpacity="0.55">
                <animateTransform attributeName="transform" type="translate" values="0,0;0,-5;0,0" dur="3.5s" begin="1.3s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"/>
                <animate attributeName="fill-opacity" values="0.55;0.9;0.55" dur="3.5s" begin="1.3s" repeatCount="indefinite"/>
              </path>
            </g>
            {/* Ground / stage */}
            <ellipse cx="140" cy="165" rx="70" ry="8" fill="white" fillOpacity="0.08"/>
            {/* Legs */}
            <path d="M 130 120 L 125 160" stroke="white" strokeOpacity="0.45" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M 150 120 L 155 160" stroke="white" strokeOpacity="0.45" strokeWidth="2.5" strokeLinecap="round"/>
            {/* Floating dots */}
            <circle cx="100" cy="170" r="2" fill="white" fillOpacity="0.3">
              <animate attributeName="cy" values="170;164;170" dur="4s" begin="0.5s" repeatCount="indefinite"/>
            </circle>
            <circle cx="185" cy="172" r="2.5" fill="white" fillOpacity="0.3">
              <animate attributeName="cy" values="172;167;172" dur="3.5s" begin="1s" repeatCount="indefinite"/>
            </circle>
            <circle cx="240" cy="145" r="3" fill="white" fillOpacity="0.25">
              <animate attributeName="cy" values="145;140;145" dur="3.8s" begin="0.3s" repeatCount="indefinite"/>
            </circle>
            <circle cx="38" cy="140" r="2.5" fill="white" fillOpacity="0.25">
              <animate attributeName="cy" values="140;135;140" dur="4.2s" begin="1.5s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>

        <div className="space-y-8">
          <div className="space-y-3">
            <h2 className="text-3xl font-bold leading-tight">
              {isCoachSignup
                ? t("auth_register_expert_headline") || "Turn your expertise into transformation"
                : t("auth_register_headline") || "Start your journey today"}
            </h2>
            <p className="text-white/75 text-lg leading-relaxed">
              {isCoachSignup
                ? t("auth_register_expert_subheadline") || "Build guided journeys your clients actually finish — with AI at your side."
                : t("auth_register_subheadline") || "Guided activities and expert coaching — designed around your goals."}
            </p>
          </div>

          <ul className="space-y-4">
            {(isCoachSignup
              ? [
                  { icon: Route, text: t("auth_register_expert_feature_1") || "The AI wizard drafts your first journey in minutes" },
                  { icon: Users, text: t("auth_register_expert_feature_2") || "Clients stay engaged with quest maps, habits, and identity growth" },
                  { icon: Zap, text: t("auth_register_expert_feature_3") || "30-day free trial — no credit card required" },
                ]
              : [
                  { icon: Sparkles, text: t("auth_register_feature_1") || "Personalized activities designed for your growth" },
                  { icon: Shield, text: t("auth_register_feature_2") || "Your data stays private and secure" },
                  { icon: Zap, text: t("auth_register_feature_3") || "Get started in seconds — no credit card required" },
                ]
            ).map(({ icon: FeatureIcon, text }, i) => (
              <li key={i} className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
                  <FeatureIcon className="h-4 w-4" />
                </span>
                <span className="text-white/90 text-sm">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-white/40 text-xs">
          © {new Date().getFullYear()} ClaryNext
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[400px] space-y-6">

          <div className="space-y-1.5 text-center">
            <h1 className="text-2xl font-bold tracking-tight">
              {showFork
                ? t("ut_gate_title") || "What brings you to ClaryNext?"
                : t("create_account") || "Create your account"}
            </h1>
            {collectionId ? (
              <div className="mt-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5">
                <p className="text-sm text-primary font-medium">
                  {loadingCollectionInfo ? (
                    <>{t("loading")}…</>
                  ) : collectionName ? (
                    <>
                      {t("joining") || "Joining"} {getLevelLabel(collectionLevel)}: <strong>{collectionName}</strong>
                    </>
                  ) : (
                    t("joining_organization") || "Joining organization"
                  )}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {showFork
                  ? t("ut_gate_subtitle") || "We'll shape the experience around it. You can change this later in your account."
                  : t("register_subtitle") || "Fill in your details to get started"}
              </p>
            )}
          </div>

          {showFork ? (
            <UserTypeCards onSelect={handleUserTypeSelect} />
          ) : (
            <>
              {!isInviteFlow && userType && (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/5 px-3 py-1 font-medium text-primary">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {userType === EXPERT_TYPE
                      ? t("ut_expert_title") || "I'm a coach or expert"
                      : t("ut_seeker_title") || "I'm here to grow"}
                  </span>
                  <button
                    type="button"
                    onClick={() => setUserType(null)}
                    className="text-muted-foreground underline-offset-4 hover:underline"
                  >
                    {t("ut_change") || "Change"}
                  </button>
                </div>
              )}

              {renderLoadingAuthMethods()}
              {renderRegistrationDisabled()}
              {renderRegisterWithPassword()}
            </>
          )}

          {!showFork && showDivider && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  {t("or")}
                </span>
              </div>
            </div>
          )}

          {!showFork && renderRegisterWithGoogle()}

          {/* Sign in link */}
          <div className="pt-2 text-center text-sm text-muted-foreground">
            {t("already_have_an_account") || "Already have an account?"}{" "}
            <Link
              to="/login"
              state={{
                acceptId,
                engagementTransfer: location.state?.engagementTransfer,
                from: location.state?.from,
              }}
              className="font-medium text-primary underline-offset-4 hover:underline transition-colors inline-flex items-center gap-0.5"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t("login")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
